// Tiny formatters used across pages.

// ── Agent display name resolution ────────────────────────────────────
// Global cache populated by any page that fetches /api/agents. Keyed by
// agent id, value is the configured display name. resolveAgentName()
// returns the cached name or a capitalized fallback.
const _agentNameCache: Record<string, string> = {};

/** Populate the display-name cache from an /api/agents response. */
export function seedAgentNames(agents: Array<{ id: string; name?: string }>): void {
  for (const a of agents) {
    if (a.id && a.name) _agentNameCache[a.id] = a.name;
  }
}

/** Resolve display name for an agent id. Returns cached name or capitalized id. */
export function resolveAgentName(agentId: string): string {
  return _agentNameCache[agentId] || agentId.charAt(0).toUpperCase() + agentId.slice(1);
}

export function formatRelativeTime(unixSeconds: number): string {
  const now = Date.now() / 1000;
  const diff = now - unixSeconds;
  if (diff < 60) return Math.max(0, Math.floor(diff)) + 's ago';
  if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
  if (diff < 86400 * 7) return Math.floor(diff / 86400) + 'd ago';
  if (diff < 86400 * 30) return Math.floor(diff / 86400 / 7) + 'w ago';
  if (diff < 86400 * 365) return Math.floor(diff / 86400 / 30) + 'mo ago';
  return Math.floor(diff / 86400 / 365) + 'y ago';
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return seconds + 's';
  if (seconds < 3600) return Math.floor(seconds / 60) + 'm';
  if (seconds < 86400) return (seconds / 3600).toFixed(1) + 'h';
  return (seconds / 86400).toFixed(1) + 'd';
}

export function formatNumber(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
}

export function formatCost(usd: number): string {
  if (usd === 0) return '$0';
  if (usd < 0.01) return '<$0.01';
  if (usd < 1) return '$' + usd.toFixed(2);
  return '$' + usd.toFixed(2);
}

// Try to JSON.parse, fall back to empty array. Memory rows return tags as
// JSON-encoded strings (per the contract notes in the ecosystem TLDR).
export function safeJsonArray<T = unknown>(s: string | null | undefined): T[] {
  if (!s) return [];
  try {
    const parsed = JSON.parse(s);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}
